import { computed, makeObservable, observable, runInAction } from 'mobx'
import type { StaticModelCollection } from '../../firestore-mobx/model'
import { SectionAssignmentPeerReviewQuestion } from '../../models/SectionAssignmentPeerReviewQuestion'
import { RoomStateUPR } from '../../models/RoomStateUPR'
import { RoomStateUPRNote } from '../../models/RoomStateUPRNote'
import {
  RoomStateUPRPeerReview,
  type PeerReviewMeaningfulScore,
  peerReviewScoreProperties,
} from '../../models/RoomStateUPRPeerReview'
import { getPeerReviewQuestionsStream } from '../../firestore/SectionAssignmentPeerReviewQuestion'
import {
  getPeerReviewNotesForUserInRoom,
  getPeerReviewNotesForRevieweeInRoom,
} from '../../firestore/RoomStateUPRNote'
import { getUserPeerReviewsForRoom } from '../../firestore/RoomStateUPR'
import {
  getPeerReviewsForRevieweeInRoom,
  getPeerReviewForRevieweeByReviewer,
} from '../../firestore/RoomStateUPRPeerReview'
import { setPeerReview } from '../../firestore/RoomStateUPRPeerReview'
import { upsertPeerReviewNote } from '../../firestore/RoomStateUPRNote'
import { deletePeerReviewNote } from '../../firestore/RoomStateUPRNote'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Cubit } from '../core'
import { guardOn, guardOnLoaded, type LoadingState } from '../../util/guardOn'
import type { PublicUser } from '../../models/PublicUser'
import { captureException } from '@sentry/core'

export class StudentPeerReviewController extends Cubit {
  repository: FirebaseRepository
  roomId: string
  userId: string
  sectionId: string | undefined
  assignmentId: string | undefined
  userIdsInRoom: string[]
  peerReviewExcludesSelfReview: boolean

  peerReviewQuestions: StaticModelCollection<SectionAssignmentPeerReviewQuestion>
  userPeerReviewRecords: StaticModelCollection<RoomStateUPR>

  peerReviewsReceived: StaticModelCollection<RoomStateUPRPeerReview>

  @observable
  selfReview: RoomStateUPRPeerReview | null = null

  @observable
  selfReviewLoaded = false

  @observable
  myReviewsLoaded = false

  peerReviewsGiven = observable.map<string, RoomStateUPRPeerReview | null>()
  notesGiven = observable.map<string, StaticModelCollection<RoomStateUPRNote>>()
  notesReceived: StaticModelCollection<RoomStateUPRNote>
  peerReviewCompletionDialogKeys = observable.set<string>()

  private hasInitializedAsReviewee = false
  private hasInitializedAsReviewer = false

  constructor(
    repository: FirebaseRepository,
    {
      roomId,
      userId,
      sectionId,
      assignmentId,
      userIdsInRoom,
      peerReviewExcludesSelfReview,
    }: {
      roomId: string
      userId: string
      sectionId: string | undefined
      assignmentId: string | undefined
      userIdsInRoom: string[]
      peerReviewExcludesSelfReview: boolean
    }
  ) {
    super()
    this.repository = repository
    this.roomId = roomId
    this.userId = userId
    this.sectionId = sectionId
    this.assignmentId = assignmentId
    this.userIdsInRoom = userIdsInRoom
    this.peerReviewExcludesSelfReview = peerReviewExcludesSelfReview

    this.peerReviewQuestions =
      SectionAssignmentPeerReviewQuestion.emptyCollection(repository)
    this.peerReviewsReceived =
      RoomStateUPRPeerReview.emptyCollection(repository)
    this.notesReceived = RoomStateUPRNote.emptyCollection(repository)
    this.selfReview = null
    this.selfReviewLoaded = this.peerReviewExcludesSelfReview
    this.myReviewsLoaded = true
    this.userPeerReviewRecords = RoomStateUPR.emptyCollection(repository)
    this.peerReviewsGiven = observable.map()
    this.notesGiven = observable.map()

    makeObservable(this)

    // When roomId is empty, the controller is inert - mark all collections as loaded. This sometimes happens in our frontend when the student is not a member of a group.
    if (!roomId) {
      this.peerReviewQuestions.replaceModels([])
      this.peerReviewsReceived.replaceModels([])
      this.userPeerReviewRecords.replaceModels([])
      this.notesReceived.replaceModels([])
      runInAction(() => {
        this.selfReviewLoaded = true
        this.myReviewsLoaded = true
      })
    }
  }

  initializeAsReviewee(): void {
    if (this.hasInitializedAsReviewee) return
    if (!this.roomId || !this.userId || !this.sectionId || !this.assignmentId) {
      return
    }

    this.hasInitializedAsReviewee = true

    this.ensureCommonPeerReviewStreams()

    this.addStream(
      getPeerReviewsForRevieweeInRoom(this.repository, {
        roomId: this.roomId,
        revieweeUserId: this.userId,
      }),
      (peerReviews) => {
        const reviewsByPeer: RoomStateUPRPeerReview[] = []
        let selfReviewFound: RoomStateUPRPeerReview | null = null

        for (const peerReview of peerReviews) {
          if (peerReview.data.userId === peerReview.data.reviewerUserId) {
            selfReviewFound = peerReview
          } else {
            reviewsByPeer.push(peerReview)
          }
        }

        runInAction(() => {
          this.peerReviewsReceived.replaceModels(reviewsByPeer)
          this.selfReview = selfReviewFound
          this.selfReviewLoaded = true
        })
      }
    )

    this.addStream(
      getPeerReviewNotesForRevieweeInRoom(this.repository, {
        roomId: this.roomId,
        userId: this.userId,
      }),
      (notes) => {
        this.notesReceived.replaceModels(notes)
      }
    )
  }

  initializeAsReviewer(): void {
    if (this.hasInitializedAsReviewer) return
    if (!this.roomId || !this.userId || !this.sectionId || !this.assignmentId) {
      return
    }

    this.hasInitializedAsReviewer = true

    this.ensureCommonPeerReviewStreams()

    this.addReaction({
      whenThisChanges: () => ({
        usersParticipatingInPeerReview: this.usersParticipatingInPeerReview,
        questionsLoaded: this.peerReviewQuestions.isLoaded,
      }),
      thenRunThisCode: ({
        usersParticipatingInPeerReview,
        questionsLoaded,
      }) => {
        if (
          !questionsLoaded ||
          this.peerReviewQuestions.length === 0 ||
          usersParticipatingInPeerReview.isLoading
        )
          return

        const currentUserIds = new Set(
          usersParticipatingInPeerReview.value.map(
            (upr: RoomStateUPR) => upr.data.userId
          )
        )

        // Clean up streams for users no longer in room
        for (const revieweeUserId of this.peerReviewsGiven.keys()) {
          if (!currentUserIds.has(revieweeUserId)) {
            const streamName = `peer-review-given-${revieweeUserId}`
            this.removeStream(streamName)
            runInAction(() => {
              this.peerReviewsGiven.delete(revieweeUserId)
              this.notesGiven.delete(revieweeUserId)
            })
          }
        }

        if (
          !this.peerReviewExcludesSelfReview &&
          !usersParticipatingInPeerReview.value.some(
            (upr: RoomStateUPR) => upr.data.userId === this.userId
          )
        ) {
          runInAction(() => {
            this.selfReviewLoaded = true
          })
          return
        }

        for (const userPeerReview of usersParticipatingInPeerReview.value) {
          const revieweeUserId = userPeerReview.data.userId

          // Skip self if self-review is not required
          if (
            this.peerReviewExcludesSelfReview &&
            revieweeUserId === this.userId
          ) {
            continue
          }

          const streamName = `peer-review-given-${revieweeUserId}`

          if (!this.hasStream(streamName)) {
            runInAction(() => {
              this.peerReviewsGiven.set(revieweeUserId, null)
            })
            this.myReviewsLoaded = false
            this.addStream(
              getPeerReviewForRevieweeByReviewer(this.repository, {
                roomId: this.roomId,
                revieweeUserId,
                reviewerUserId: this.userId,
              }),
              (peerReview) => {
                runInAction(() => {
                  if (revieweeUserId === this.userId) {
                    this.selfReview = peerReview
                    this.selfReviewLoaded = true
                  } else {
                    this.peerReviewsGiven.set(revieweeUserId, peerReview)
                  }
                  this.myReviewsLoaded = true
                })
              },
              { name: streamName }
            )

            if (!this.notesGiven.has(revieweeUserId)) {
              runInAction(() => {
                this.notesGiven.set(
                  revieweeUserId,
                  RoomStateUPRNote.emptyCollection(this.repository)
                )
              })
            }

            const notesStreamName = `peer-review-notes-given-${revieweeUserId}`
            if (!this.hasStream(notesStreamName)) {
              this.addStream(
                getPeerReviewNotesForUserInRoom(this.repository, {
                  roomId: this.roomId,
                  userId: revieweeUserId,
                  reviewerUserId: this.userId,
                }),
                (notes) => {
                  const notesCollection = this.notesGiven.get(revieweeUserId)
                  if (notesCollection) {
                    notesCollection.replaceModels(notes)
                  }
                },
                { name: notesStreamName }
              )
            }
          }
        }
      },
      fireImmediately: true,
    })
  }

  private ensureCommonPeerReviewStreams(): void {
    this.startPeerReviewQuestionsStream()
    this.restartUsersParticipatingStream()
  }

  private startPeerReviewQuestionsStream(retryCount: number = 0): void {
    const questionsStreamKey = `peer-review-questions-${this.sectionId}-${this.assignmentId}`
    if (this.hasStream(questionsStreamKey))
      this.removeStream(questionsStreamKey)
    if (!this.sectionId || !this.assignmentId || this.isDisposed) return

    this.addStream(
      getPeerReviewQuestionsStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (questions) => {
        const sortedQuestions = [...questions].sort(
          (a, b) => a.data.prOrder - b.data.prOrder
        )
        this.peerReviewQuestions.replaceModels(sortedQuestions)
      },
      {
        name: questionsStreamKey,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 3) {
            const delay = 400 * retryCount
            setTimeout(() => {
              this.startPeerReviewQuestionsStream(retryCount + 1)
            }, delay)
          } else {
            console.error('Error loading peer review questions', err)
            captureException(err, { tags: { feature: 'peer-review' } })
          }
        },
      }
    )
  }

  private restartUsersParticipatingStream(retryCount: number = 0): void {
    const usersStreamKey = `users-participating-in-peer-review-${this.roomId}`
    if (this.hasStream(usersStreamKey)) this.removeStream(usersStreamKey)
    if (!this.roomId || this.isDisposed) return

    this.addStream(
      getUserPeerReviewsForRoom(this.repository, {
        roomId: this.roomId,
      }),
      (userPeerReviews) => {
        this.userPeerReviewRecords.replaceModels(userPeerReviews)
      },
      {
        name: usersStreamKey,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 3) {
            const delay = 400 * retryCount
            setTimeout(() => {
              this.restartUsersParticipatingStream(retryCount + 1)
            }, delay)
          } else {
            console.error('Error loading user peer reviews for room', err)
            captureException(err, { tags: { feature: 'peer-review' } })
          }
        },
      }
    )
  }

  async submitPeerReview(
    revieweeUserId: string,
    review: Record<string, number>
  ): Promise<void> {
    if (!this.sectionId || !this.assignmentId) return
    await setPeerReview(this.repository, {
      roomId: this.roomId,
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      userId: revieweeUserId,
      reviewerUserId: this.userId,
      review,
    })
  }

  async upsertPeerReviewNote(
    revieweeUserId: string,
    reviewNote: string,
    sharedWithPeer: boolean
  ): Promise<void> {
    if (!this.sectionId || !this.assignmentId) return
    await upsertPeerReviewNote(this.repository, {
      roomId: this.roomId,
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      userId: revieweeUserId,
      reviewerUserId: this.userId,
      reviewNote,
      sharedWithPeer,
    })
  }

  async deletePeerReviewNote(
    revieweeUserId: string,
    noteId: string
  ): Promise<void> {
    await deletePeerReviewNote(this.repository, {
      roomId: this.roomId,
      userId: revieweeUserId,
      reviewerUserId: this.userId,
      noteId,
    })
  }

  @computed
  get peerReviewsEnabled(): LoadingState<boolean> {
    return guardOnLoaded(this.peerReviewQuestions, (questions) => {
      return questions.length > 0
    })
  }

  @computed
  get usersParticipatingInPeerReview(): LoadingState<RoomStateUPR[]> {
    return guardOnLoaded([this.userPeerReviewRecords], ([userPeerReviews]) => {
      return userPeerReviews.filter((userPeerReview) => {
        return this.userIdsInRoom.includes(userPeerReview.data.userId)
      })
    })
  }

  @computed
  get reviewsByPeersOfCurrentUserExcludingNA(): LoadingState<ReviewsByPeersOfCurrentUser> {
    return guardOnLoaded(
      [
        this.peerReviewsReceived,
        this.peerReviewQuestions,
        this.usersParticipatingInPeerReview,
      ],
      ([
        peerReviewsReceived,
        peerReviewQuestions,
        validUserPeerReviewRecords,
      ]) => {
        const totalExpectedReviewsByPeers =
          validUserPeerReviewRecords.length - 1 // Exclude self

        const questionsWithAverages: PeerReviewQuestionWithAverage[] =
          peerReviewQuestions.map((question) => {
            let sumOfScores = 0
            let scoresCount = 0

            for (const peerReview of peerReviewsReceived) {
              const review = peerReview.data.review
              const score = review[question.id]

              if (score !== undefined && score !== 0) {
                sumOfScores += score
                scoresCount += 1
              }
            }

            const averageScore =
              scoresCount > 0
                ? Math.round((sumOfScores / scoresCount) * 10) / 10
                : 0

            return {
              question,
              averageScore,
            }
          })

        return {
          peerReviewersCount: peerReviewsReceived.length,
          totalExpectedReviewsByPeers,
          questionsWithAverages,
        }
      }
    )
  }

  @computed
  get usersDeservingReviews() {
    return guardOnLoaded(
      [this.usersParticipatingInPeerReview],
      ([validUserPeerReviewRecords]) => {
        if (this.peerReviewExcludesSelfReview) {
          return validUserPeerReviewRecords.filter(
            (upr) => upr.data.userId !== this.userId
          )
        }

        return validUserPeerReviewRecords
      }
    )
  }

  @computed
  get peerReviewsGivenByCurrentUser(): LoadingState<
    Map<string, RoomStateUPRPeerReview | null>
  > {
    return guardOnLoaded(
      [this.usersParticipatingInPeerReview, this.peerReviewQuestions],
      () => {
        return this.peerReviewsGiven
      }
    )
  }

  @computed
  get reviewsOfUserByReviewee(): LoadingState<RevieweeToReview> {
    return guardOnLoaded(
      [
        this.peerReviewsGivenByCurrentUser,
        this.usersDeservingReviews,
        this.peerReviewQuestions,
      ],
      ([peerReviewsGiven, usersDeservingReviews]) => {
        const map = new Map<
          string,
          {
            review: Map<string, number>
            note?: string
            sharedWithPeer?: boolean
          }
        >()

        for (const userToReview of usersDeservingReviews) {
          const userId = userToReview.data.userId
          const isSelfReview = userId === this.userId

          let review: RoomStateUPRPeerReview | null = null
          if (isSelfReview && !this.peerReviewExcludesSelfReview) {
            review = this.selfReview
          } else {
            review = peerReviewsGiven.get(userId) ?? null
          }

          if (review && review.data) {
            const reviewMap = new Map<string, number>()
            for (const [questionId, score] of Object.entries(
              review.data.review
            )) {
              reviewMap.set(questionId, score)
            }
            const notes = this.notesGiven.get(userId)
            const note = notes?.models[0]?.data.reviewNote
            const sharedWithPeer =
              notes?.models[0]?.data.sharedWithPeer ?? false
            map.set(userId, {
              review: reviewMap,
              note,
              sharedWithPeer,
            })
          } else {
            // User hasn't been reviewed yet, but still include them
            map.set(userId, {
              review: new Map(),
            })
          }
        }

        return map
      }
    )
  }

  @computed
  get reviewees() {
    return guardOnLoaded(
      [this.reviewsOfUserByReviewee],
      ([reviewsOfUserByReviewee]) => {
        return Array.from(reviewsOfUserByReviewee.keys())
          .flatMap((userId) => {
            const reviewData = reviewsOfUserByReviewee.get(userId)
            const user = this.repository.userStore.getUser(userId)
            return user && reviewData
              ? [
                  {
                    user,
                    reviewData,
                    isCurrentUser: userId === this.userId,
                  },
                ]
              : []
          })
          .sort((a, b) => {
            // Sort current user to the bottom if they exist
            if (a.isCurrentUser && !b.isCurrentUser) return 1
            if (!a.isCurrentUser && b.isCurrentUser) return -1
            // Then sort alphabetically by name
            return a.user.fullName.localeCompare(b.user.fullName)
          })
      }
    )
  }

  @computed
  get userIsInPeerReview(): LoadingState<boolean> {
    return guardOnLoaded(
      [this.peerReviewsEnabled, this.usersParticipatingInPeerReview],
      ([peerReviewsEnabled, validUserPeerReviewRecords]) => {
        if (!peerReviewsEnabled) return false

        const hasUPREntry = validUserPeerReviewRecords.some(
          (upr) => upr.data.userId === this.userId
        )
        return hasUPREntry
      }
    )
  }

  @computed
  get userNeedsToStartPeerReview(): LoadingState<boolean> {
    return guardOnLoaded(
      [
        this.userYetToCompletePeerReview,
        this.peerReviewsGivenByCurrentUser,
        this.usersDeservingReviews,
      ],
      ([
        userYetToCompletePeerReview,
        peerReviewsGiven,
        usersDeservingReviews,
      ]) => {
        if (!userYetToCompletePeerReview) return false

        // Check if user has ANY peer review scores given — if so, they do not need to start it.
        let hasAnyReviews = false
        for (const userToReview of usersDeservingReviews) {
          const review = peerReviewsGiven.get(userToReview.data.userId)
          if (review && review.data) {
            const reviewData = review.data.review
            for (const question of this.peerReviewQuestions.models) {
              const score = reviewData[question.id]
              if (score !== undefined && score !== 0) {
                hasAnyReviews = true
                break
              }
            }
            if (hasAnyReviews) break
          }
        }

        return !hasAnyReviews
      }
    )
  }

  @computed
  get selfReviewLoadingState(): LoadingState<RoomStateUPRPeerReview | null> {
    return guardOn(
      this.selfReviewLoaded || this.peerReviewExcludesSelfReview,
      () => {
        return this.selfReview
      }
    )
  }

  @computed
  get myReviewsLoadedState(): LoadingState<null> {
    if (this.myReviewsLoaded)
      return { isLoading: false, isLoaded: true, value: null }

    return { isLoading: true, isLoaded: false }
  }

  @computed
  get userYetToCompletePeerReview(): LoadingState<boolean> {
    const userIsInPeerReviewState = this.userIsInPeerReview

    if (userIsInPeerReviewState.isLoading) {
      return { isLoading: true, isLoaded: false }
    }

    if (!userIsInPeerReviewState.value) {
      return { isLoading: false, isLoaded: true, value: false }
    }

    return guardOnLoaded(
      [
        this.peerReviewsGivenByCurrentUser,
        this.peerReviewQuestions,
        this.usersDeservingReviews,
        this.selfReviewLoadingState,
        this.myReviewsLoadedState,
      ],
      ([
        peerReviewsGivenByCurrentUser,
        peerReviewQuestions,
        usersDeservingReviews,
        selfReview,
      ]) => {
        if (peerReviewQuestions.length === 0) {
          return false
        }

        if (usersDeservingReviews.length === 0) {
          return false
        }
        if (!this.peerReviewExcludesSelfReview) {
          if (selfReview && selfReview.data) {
            return false
          }
        }

        for (const userToReview of usersDeservingReviews) {
          const peerReview = peerReviewsGivenByCurrentUser.get(
            userToReview.data.userId
          )
          if (peerReview && peerReview.data) {
            return false
          }
        }
        return true
      }
    )
  }

  hasMissingReviews(
    stagedReviews: Map<string, Map<string, PeerReviewMeaningfulScore>>
  ): LoadingState<boolean> {
    return guardOnLoaded(
      [
        this.peerReviewQuestions,
        this.usersDeservingReviews,
        this.reviewsOfUserByReviewee,
      ],
      ([peerReviewQuestions, usersDeservingReviews, reviewsByReviewee]) => {
        for (const userToReview of usersDeservingReviews) {
          const userId = userToReview.data.userId

          for (const question of peerReviewQuestions) {
            const stagedScore = stagedReviews.get(userId)?.get(question.id)
            if (stagedScore !== undefined) {
              continue
            }

            const existingReview = reviewsByReviewee.get(userId)
            const existingScore = existingReview?.review.get(question.id)

            if (existingScore === undefined) {
              return true
            }
          }
        }

        return false
      }
    )
  }

  async saveStagedPeerReviewsAndNotes(params: {
    /** Map<revieweeUserId, Map<questionId, PeerReviewMeaningfulScore>> */
    stagedReviews: Map<string, Map<string, PeerReviewMeaningfulScore>>
    /** Map<revieweeUserId, note> */
    stagedNotes: Map<string, string>
    /** Map<revieweeUserId, sharedWithPeer> */
    stagedSharing: Map<string, boolean>
    reviewsOfUserByReviewee: RevieweeToReview
  }): Promise<void> {
    const {
      stagedReviews,
      stagedNotes,
      stagedSharing,
      reviewsOfUserByReviewee,
    } = params
    const savePromises: Promise<void>[] = []

    for (const [revieweeUserId, questionScores] of stagedReviews.entries()) {
      const existingReview = reviewsOfUserByReviewee.get(revieweeUserId)?.review
      const reviewToSave: Record<string, number> = {}

      if (existingReview) {
        for (const [questionId, score] of existingReview.entries()) {
          reviewToSave[questionId] = score
        }
      }

      for (const [questionId, scoreOption] of questionScores.entries()) {
        reviewToSave[questionId] =
          peerReviewScoreProperties[scoreOption].DBValue
      }

      savePromises.push(this.submitPeerReview(revieweeUserId, reviewToSave))
    }

    for (const [revieweeUserId, note] of stagedNotes.entries()) {
      const existingData = reviewsOfUserByReviewee.get(revieweeUserId)
      const existingNote = existingData?.note
      const existingShared = existingData?.sharedWithPeer ?? false
      const newShared = stagedSharing.get(revieweeUserId) ?? existingShared

      if (note === '') {
        const notes = this.notesGiven.get(revieweeUserId)
        if (notes?.models[0]) {
          savePromises.push(
            this.deletePeerReviewNote(revieweeUserId, notes.models[0].id)
          )
        }
      } else if (note !== existingNote || newShared !== existingShared) {
        savePromises.push(
          this.upsertPeerReviewNote(revieweeUserId, note, newShared)
        )
      }
    }

    // Handle sharing-only changes for notes not in stagedNotes
    for (const [revieweeUserId, newShared] of stagedSharing.entries()) {
      if (stagedNotes.has(revieweeUserId)) continue // already handled above
      const existingData = reviewsOfUserByReviewee.get(revieweeUserId)
      const existingNote = existingData?.note
      const existingShared = existingData?.sharedWithPeer ?? false
      if (existingNote && newShared !== existingShared) {
        savePromises.push(
          this.upsertPeerReviewNote(revieweeUserId, existingNote, newShared)
        )
      }
    }

    await Promise.all(savePromises)
  }

  getPeerReviewGivenForUser(
    revieweeUserId: string
  ): RoomStateUPRPeerReview | null {
    return this.peerReviewsGiven.get(revieweeUserId) ?? null
  }

  getNotesGivenForUser(
    revieweeUserId: string
  ): StaticModelCollection<RoomStateUPRNote> {
    return (
      this.notesGiven.get(revieweeUserId) ??
      RoomStateUPRNote.emptyCollection(this.repository)
    )
  }

  @computed
  get sharedNotesForCurrentUser(): LoadingState<RoomStateUPRNote[]> {
    return guardOnLoaded(this.notesReceived, (notes) =>
      notes.filter((note) => note.data.sharedWithPeer === true)
    )
  }
}

type PeerReviewQuestionWithAverage = {
  question: SectionAssignmentPeerReviewQuestion
  averageScore: number
}

type ReviewsByPeersOfCurrentUser = {
  peerReviewersCount: number
  totalExpectedReviewsByPeers: number
  questionsWithAverages: PeerReviewQuestionWithAverage[]
}

export type RevieweeToReview = Map<
  string,
  { review: Map<string, number>; note?: string; sharedWithPeer?: boolean }
>

export type RevieweeForPeerReviewGrid = {
  user: PublicUser
  reviewData: {
    review: Map<string, number>
    note?: string
    sharedWithPeer?: boolean
  }
  isCurrentUser: boolean
}
